import { Spinner } from 'components/spinner';
import { useGetLiveStreamingQuery } from 'features/api/sportApiSlice';
import { selectStreamingMode, selectStreamingRoots } from 'features/settings/selectors';
import { setStreamingMode as _setStreamingMode, setStreamingRoot } from 'features/settings/settingsSlice';
import { useInternalSession } from 'hooks/useInternalSession';
import { useTranslation } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { SportLiveStreamingRequestApi } from 'lib/api/sport/sportLiveStreamingRequestApi';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { SportsStreamingProviderEnum, SportsStreamingProviderType } from 'types/swagger';
import { breakpoint, isClientSide, isServerSide } from 'utility/constant';
import { isTruthy } from 'utility/functions';
import { LIVE_STREAMING_ID, LiveVideoStreaming, MAX_HEIGHT, MAX_WIDTH, StreamingControl, VideoStreamingMode } from '.';

export interface LiveStreamingProps extends SportLiveStreamingRequestApi {
  hasStreaming?: boolean;
}

export interface LiveStreamingHookType {
  isEmpty?: boolean;
  isError?: boolean;
  isMobile?: boolean;
  isLoading: boolean;
  providerType?: SportsStreamingProviderType;
  streamingMode: VideoStreamingMode;
  isAuthenticated: boolean;
  setStreamingMode: (_nextValue: VideoStreamingMode) => void;
}

interface LiveStreamingState {
  mode: VideoStreamingMode;
  isEmpty: boolean;
  streamingUrl?: string;
}

export const useLiveStreaming = ({
  betGeniusMatchId,
  betRadarMatchId,
  idAvvenimento,
  idProgramma,
  hasStreaming,
}: LiveStreamingProps): LiveStreamingHookType => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isMobile: isMobileViewport } = useWindowSize();

  const [st, setSt] = useState<LiveStreamingState>({
    isEmpty: false,
    mode: VideoStreamingMode.off,
  });

  const { mode, isEmpty } = st;
  const roots = useTypedSelector(selectStreamingRoots);
  const prevMode = useTypedSelector(selectStreamingMode);
  const containerRef = useRef<Document | null>(isServerSide ? null : document);
  const { isAuthenticated } = useInternalSession();

  const unmountRoot = useCallback(
    (element: Root) => {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      element.render(<React.Fragment />);
      setTimeout(() => {
        element.unmount();
        dispatch(setStreamingRoot({ mode: prevMode, delete: true }));
      }, 100);
    },
    [dispatch, prevMode]
  );

  // trigger or not api call
  const skip = useMemo(() => {
    if (isServerSide) return true;
    if (!isAuthenticated) return true;
    if (!hasStreaming) return true;
    return false;
  }, [hasStreaming, isAuthenticated]);

  // fetch result
  const { streamingUrl, providerType, isError, isLoading } = useGetLiveStreamingQuery(
    {
      betGeniusMatchId,
      betRadarMatchId,
      idAvvenimento,
      idProgramma,
    },
    {
      selectFromResult: ({ data, isError, isLoading }) => {
        const { url, type } = data ?? {};
        return { streamingUrl: url, providerType: type, isError, isLoading };
      },
      skip,
    }
  );

  // layouts attributes
  const { isMobile, width, height } = useMemo(() => {
    let videoH = 0;
    let videoW = 0;
    let isMobile = false;

    if (isClientSide) {
      const { width, height } = screen ?? {};

      const maxSize = (width ?? 0) < (height ?? 0) ? height : width;
      isMobile = maxSize! < breakpoint.md;

      videoW = MAX_WIDTH;
      videoH = MAX_HEIGHT;

      if (isMobile && (width ?? 0) > (height ?? 0)) {
        videoH = MAX_WIDTH;
        videoW = MAX_HEIGHT;
      }

      if (providerType && [SportsStreamingProviderEnum.Betradar].includes(providerType)) {
        if (isMobile) {
          videoW = (maxSize ?? 0) * 0.5;
          videoH = (maxSize ?? 0) * 0.5;
        } else {
          videoW = (width ?? 0) * 0.5;
          videoH = (height ?? 0) * 0.5;
        }
      }
    }

    return {
      isMobile,
      width: videoW,
      height: videoH,
    };
  }, [providerType]);

  // mode-change event handler
  const setStreamingMode = useCallback(
    (nextValue: VideoStreamingMode): void => {
      if (isMobile && nextValue === VideoStreamingMode.minimal) {
        nextValue = VideoStreamingMode.expanded;
      }
      setSt((st) => ({ ...st, mode: nextValue }));
    },
    [isMobile]
  );

  // detect empty url
  useEffect(() => {
    if (skip) return;

    let nextIsEmpty = false;

    if (!isLoading) {
      nextIsEmpty = isTruthy(streamingUrl?.length) ? false : true;
    }

    if (nextIsEmpty !== isEmpty) {
      setSt((st) => ({ ...st, isEmpty: nextIsEmpty }));
    }
  }, [skip, isEmpty, streamingUrl, isLoading, mode]);

  // compute video style attributes
  const style = useMemo(() => {
    const defaultStyle = {
      maxHeight: `${height}px`,
      maxWidth: `${width}px`,
    };
    // console.log('🚀 ~ style ~ isMobile:', isMobileViewport);
    if (isMobileViewport) {
      return defaultStyle;
    }
    return mode === VideoStreamingMode.modal
      ? defaultStyle
      : {
          ...defaultStyle,
          marginLeft: '50%',
          transform: 'translateX(-50%)',
        };
  }, [height, isMobileViewport, mode, width]);

  // when mode changes, set root element
  useEffect(() => {
    if (skip) return;
    if (prevMode === mode) return;
    const rootPrev = roots[prevMode];

    if (rootPrev) {
      unmountRoot(rootPrev);
    }

    if (mode === VideoStreamingMode.modal) {
      const newWindow = window.open('', '_blank', `width=${width},height=${height},resizable=0,popup=1`);
      containerRef.current = newWindow!.document;
      newWindow!.onbeforeunload = () => {
        setStreamingMode(VideoStreamingMode.minimal);
      };

      const objStyle = containerRef.current.createElement('style');
      objStyle.innerHTML = `html, body { margin: 0; padding: 0; }`;
      containerRef.current.head.appendChild(objStyle);

      containerRef.current.title = t('live-streaming-video', 'Streaming Video') ?? 'Streaming Video';
      containerRef.current.body.innerHTML = `<div id="${VideoStreamingMode.modal}-${LIVE_STREAMING_ID}" />`;
    } else {
      containerRef.current = document;
    }

    // Gestisci il caso in cui mode è off
    if (mode === VideoStreamingMode.off) {
      [VideoStreamingMode.minimal, VideoStreamingMode.expanded, VideoStreamingMode.modal].forEach((currentMode) => {
        const element = roots[currentMode];
        if (element) {
          unmountRoot(element);
        }
      });
      dispatch(_setStreamingMode(mode));
      return;
    }
    const obj = containerRef.current.getElementById(`${mode}-${LIVE_STREAMING_ID}`);
    if (!obj) {
      throw new Error(`Missing streaming container for : ${mode}!`);
    }
    obj.style.display = 'block';
    obj.style.visibility = 'visible';

    const rootNext = createRoot(obj);

    rootNext.render(<LiveVideoStreaming style={{ ...style, display: 'none', visibility: 'hidden' }} urlStreaming="" />);

    dispatch(setStreamingRoot({ mode, root: rootNext }));
    dispatch(_setStreamingMode(mode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, height, mode, setStreamingMode, skip, style, t, width]);

  // render content
  useEffect(() => {
    if (skip) return;
    if (!roots[mode]) return;

    if (isLoading) {
      roots[mode].render(
        <div style={style}>
          <Spinner className={'LiveVideoStreaming'} />
        </div>
      );
    } else if (isEmpty || isError) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      roots[mode].render(<React.Fragment />);
    } else if (streamingUrl) {
      roots[mode].render(
        <React.StrictMode>
          <LiveVideoStreaming
            style={style}
            urlStreaming={streamingUrl}
            isFullScreen={mode === VideoStreamingMode.modal}
            availableControl={[StreamingControl.nofullscreen, StreamingControl.nodownload]}
          />
        </React.StrictMode>
      );
    }
  }, [skip, mode, style, isError, isEmpty, isLoading, streamingUrl, roots]);

  return {
    isError,
    isEmpty,
    isMobile,
    isLoading,
    providerType,
    streamingMode: mode,
    isAuthenticated,
    setStreamingMode,
  };
};
